import express, { Request, Response, NextFunction } from "express";
import cors from "cors";
import type { Server } from "http";
import { ParsedSpec, MockServerOptions, MockAPIServer } from "./types";
import { generateMockData } from "./generator";

type CollectionStore = Map<string, any[]>;

export function createMockServer(
  parsed: ParsedSpec,
  options: MockServerOptions = {}
): MockAPIServer {
  const app = express();
  const port = options.port ?? 3000;
  const errorRate = options.errorRate ?? 0;
  const store: CollectionStore = new Map();
  const verbose = (options as any).verbose ?? false;

  app.use(cors());
  app.use(express.json());

  // Verbose logging middleware
  if (verbose) {
    app.use((req: Request, _res: Response, next: NextFunction) => {
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] ${req.method} ${req.path}`);
      if (Object.keys(req.query).length > 0) {
        console.log(`  Query:`, req.query);
      }
      if (req.body && Object.keys(req.body).length > 0) {
        console.log(`  Body:`, JSON.stringify(req.body, null, 2));
      }
      next();
    });
  }

  // Delay / slow-network middleware
  app.use(async (_req: Request, _res: Response, next: NextFunction) => {
    const delay = computeDelay(options);
    if (delay > 0) {
      setTimeout(next, delay);
    } else {
      next();
    }
  });

  // Register routes
  for (const route of parsed.routes) {
    const expressPath = toExpressPath(route.path);

    const handler = (req: Request, res: Response) => {
      // Random error responses
      if (errorRate > 0 && Math.random() < errorRate) {
        const codes = [401, 404, 500];
        const code = codes[Math.floor(Math.random() * codes.length)]!;
        return res.status(code).json({ error: `Mock error ${code}` });
      }

      try {
        const basePath = getBasePath(route.path);
        const isItemRoute = hasPathParam(route.path);

        if (isCollectionMethod(route.method) && !isItemRoute) {
          return handleCollectionRoute(req, res, route, basePath, store, options);
        }

        if (!isCollectionMethod(route.method) && isItemRoute) {
          return handleItemRoute(req, res, route, basePath, store);
        }

        // Fallback: stateless generation
        const payload = generateMockData(route.responseSchema, {
          generators: options.generators
        });
        const status = route.statusCode ?? 200;
        res.status(status).json(payload);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error while generating mock data.";
        res.status(500).json({ error: "Failed to generate mock response", details: message });
      }
    };

    switch (route.method) {
      case "get":
        app.get(expressPath, handler);
        break;
      case "post":
        app.post(expressPath, handler);
        break;
      case "put":
        app.put(expressPath, handler);
        break;
      case "delete":
        app.delete(expressPath, handler);
        break;
      case "patch":
        app.patch(expressPath, handler);
        break;
      default:
        break;
    }
  }

  let server: Server | null = null;

  return {
    async start() {
      if (server) return;
      await new Promise<void>((resolve, reject) => {
        server = app
          .listen(port, () => resolve())
          .on("error", (err) => {
            server = null;
            reject(err);
          });
      });
    },
    async stop() {
      if (!server) return;
      await new Promise<void>((resolve, reject) => {
        server!.close((err) => {
          if (err) reject(err);
          else resolve();
        });
      });
      server = null;
    },
    getApp() {
      return app;
    }
  };
}

function computeDelay(options: MockServerOptions): number {
  if (options.delayRange) {
    const { min, max } = options.delayRange;
    if (max <= min) return min;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
  return options.delay ?? 0;
}

function toExpressPath(path: string): string {
  // Convert OpenAPI-style {id} params to Express :id
  return path.replace(/\{([^}]+)\}/g, ":$1");
}

function getBasePath(path: string): string {
  // Remove path params like /users/{id} -> /users
  return path.replace(/\/\{[^}]+\}/g, "");
}

function hasPathParam(path: string): boolean {
  return /\{[^}]+\}/.test(path);
}

function isCollectionMethod(method: string): boolean {
  // Collection-level routes are typically GET/POST on the base path.
  return method === "get" || method === "post";
}

function handleCollectionRoute(
  req: Request,
  res: Response,
  route: ParsedSpec["routes"][number],
  basePath: string,
  store: CollectionStore,
  options: MockServerOptions
) {
  const status = route.statusCode ?? 200;

  let collection = store.get(basePath);
  if (!collection) {
    // Start with an empty collection; data will be added via POST/PUT/PATCH.
    collection = [];
    store.set(basePath, collection);
  }

  switch (route.method) {
    case "get": {
      let results = collection;

      // Filtering: equality on query params (excluding pagination params)
      const { page, limit, ...filters } = req.query as Record<string, string>;
      results = results.filter((item) => {
        return Object.entries(filters).every(([key, value]) => {
          return String((item as any)[key]) === String(value);
        });
      });

      // Pagination
      const pageNum = page ? parseInt(String(page), 10) || 1 : 1;
      const limitNum = limit ? parseInt(String(limit), 10) || 20 : results.length;
      const start = (pageNum - 1) * limitNum;
      const end = start + limitNum;
      const paged = results.slice(start, end);

      return res.status(status).json(paged);
    }
    case "post": {
      const body = req.body ?? {};
      const newItem = { ...body };
      // Ensure an id exists
      if (newItem.id == null) {
        newItem.id = collection.length + 1;
      }
      collection.push(newItem);
      return res.status(201).json(newItem);
    }
    default:
      // For unsupported combinations fall back to generated response
      const payload = generateMockData(route.responseSchema, {
        generators: options.generators
      });
      return res.status(status).json(payload);
  }
}

function handleItemRoute(
  req: Request,
  res: Response,
  route: ParsedSpec["routes"][number],
  basePath: string,
  store: CollectionStore
) {
  const status = route.statusCode ?? 200;
  const idParamMatch = route.path.match(/\{([^}]+)\}/);
  const idParam = idParamMatch ? idParamMatch[1] : "id";

  const collection = store.get(basePath) ?? [];
  const idValue = req.params[idParam];

  const index = collection.findIndex((item) => {
    const candidate = (item as any)[idParam] ?? (item as any).id;
    return String(candidate) === String(idValue);
  });

  switch (route.method) {
    case "get": {
      if (index === -1) {
        return res.status(404).json({ error: "Not found" });
      }
      const item = collection[index];
      // Ensure the returned item has an ID field so that consumers relying on it
      // (and tests) can consistently access it, even if the original payload
      // did not include an id and it was inferred from the URL.
      if ((item as any)[idParam] == null && (item as any).id == null) {
        (item as any)[idParam] = idValue;
      }
      return res.status(status).json(item);
    }
    case "put": {
      if (index === -1) {
        return res.status(404).json({ error: "Not found" });
      }
      const body = req.body ?? {};
      collection[index] = { ...body, [idParam]: (collection[index] as any)[idParam] };
      return res.status(status).json(collection[index]);
    }
    case "patch": {
      if (index === -1) {
        return res.status(404).json({ error: "Not found" });
      }
      const body = req.body ?? {};
      collection[index] = { ...collection[index], ...body };
      return res.status(status).json(collection[index]);
    }
    case "delete": {
      if (index === -1) {
        return res.status(404).json({ error: "Not found" });
      }
      collection.splice(index, 1);
      return res.status(204).send();
    }
    default:
      return res.status(405).json({ error: "Method not allowed" });
  }
}
